import { useState, useContext, useRef, useEffect } from "react";
import { DateTime } from "luxon";
import Popup from "../Elements/Popup";
import { TbEdit, TbCirclePlus, TbTrash, TbEye } from "react-icons/tb";
import Card from "./Card";
import InputText from "../Inputs/InputText";
import InputTextarea from "../Inputs/InputTextarea";
import animation from "../../Animations/notes-animation.json";
import InputDate from "../Inputs/InputDate";
import NoteItem from "../Elements/NoteItem";
import { deleteNote, submitNote, updateNote } from "../../api";
import { AuthContext } from "../../context/context";
import { useLanguage } from "../../context/LanguageContext";
import { z } from "zod";
import Toast from "../Elements/Toast";
import PropTypes from "prop-types";

const noteSchema = z.object({
  note_title: z
    .string()
    .min(1, "Title is required")
    .max(200, "Title must be 200 characters or less"),
  note_description: z
    .string()
    .min(1, "Description is required")
    .max(3000, "Description must be 3000 characters or less"),
  note_date: z.string().min(1, "Date is required"),
});

const CardAddNote = ({ notes = [], activeDay, descriptionClassName, onExternalTrigger, onExternalViewTrigger, cardClassName, useMaxHeight }) => {
  const { userData, setUserData } = useContext(AuthContext);
  const { copy } = useLanguage();
  const noteButtonRef = useRef(null);
  const [popupType, setPopupType] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const [note_title, setNoteTitle] = useState("");
  const [note_description, setNoteDescription] = useState("");
  const [note_date, setNoteDate] = useState(
    activeDay ? activeDay.toISODate() : DateTime.local().toISODate()
  );
  const [noteErrors, setNoteErrors] = useState({});

  const [noteToEdit, setNoteToEdit] = useState(null);
  const [editedNoteDate, setEditedNoteDate] = useState(null);
  const [editedNoteTitle, setEditedNoteTitle] = useState("");
  const [editedNoteText, setEditedNoteText] = useState("");
  const [editNoteErrors, setEditNoteErrors] = useState({});

  const [noteToDelete, setNoteToDelete] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);

  // Handle external triggers
  useEffect(() => {
    if (onExternalTrigger) {
      openPopup("note");
    }
  }, [onExternalTrigger]);

  // Handle external view triggers
  useEffect(() => {
    if (onExternalViewTrigger && notes.length > 0) {
      const noteToView = notes[0]; // Get the first (and only) note for the day
      handleReadClickNote(noteToView);
    }
  }, [onExternalViewTrigger, notes]);

  const openPopup = (type) => {
    if (type === "note") {
      setNoteTitle("");
      setNoteDescription("");
      setNoteDate(
        activeDay ? activeDay.toISODate() : DateTime.local().toISODate()
      );
      setNoteErrors({});
    }
    setPopupType(type);
  };

  const closePopup = () => {
    setPopupType(null);
    setSelectedNote(null);
    setNoteErrors({});
    setEditNoteErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = noteSchema.safeParse({
      note_title,
      note_description,
      note_date,
    });

    if (!result.success) {
      const formatted = result.error.format();
      setNoteErrors({
        note_title: formatted.note_title?._errors?.[0] || "",
        note_description: formatted.note_description?._errors?.[0] || "",
        note_date: formatted.note_date?._errors?.[0] || "",
      });
      return;
    }

    try {
      const newNote = await submitNote(
        note_title,
        note_description,
        note_date,
        userData.id
      );
      if (!newNote) {
        setToast({
          message: copy.card_note.errors.submit_failed,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      newUserData.notes = [...userData.notes, newNote];
      setUserData(newUserData);

      setToast({
        message: copy.card_note.errors.submit_success,
        type: "success",
      });
      closePopup();
    } catch (err) {
      console.error("Error adding note:", err.message);
      setToast({ message: copy.card_note.errors.submit_error, type: "error" });
    }
  };

  const handleDeleteClick = (noteId) => {
    setNoteToDelete(noteId);
    openPopup("note-delete");
  };

  const handleDelete = async () => {
    if (!noteToDelete) {
      setToast({ message: copy.card_note.errors.not_found, type: "error" });
      return;
    }

    try {
      const result = await deleteNote(noteToDelete);
      if (!result) {
        setToast({
          message: copy.card_note.errors.delete_failed,
          type: "error",
        });
        return;
      }

      const newUserData = {
        ...userData,
        notes: userData.notes.filter((q) => q.id !== noteToDelete),
      };
      setUserData(newUserData);

      setToast({
        message: copy.card_note.errors.delete_success,
        type: "success",
      });
      setNoteToDelete(null);
      closePopup();
    } catch (err) {
      console.error("Error deleting note:", err.message);
      setToast({ message: copy.card_note.errors.delete_failed, type: "error" });
    }
  };

  const handleEditClickNote = (noteItem) => {
    if (!noteItem) return;
    setNoteToEdit(noteItem.id);
    setEditedNoteTitle(noteItem.note_title);
    setEditedNoteText(noteItem.note_description);
    setEditedNoteDate(noteItem.note_date);
    setSelectedNote(null);
    setEditNoteErrors({});
    openPopup("note-edit");
  };

  const handleEditNote = async () => {
    const result = noteSchema.safeParse({
      note_title: editedNoteTitle,
      note_description: editedNoteText,
      note_date: editedNoteDate,
    });

    if (!result.success) {
      const formatted = result.error.format();
      setEditNoteErrors({
        note_title: formatted.note_title?._errors?.[0] || "",
        note_description: formatted.note_description?._errors?.[0] || "",
        note_date: formatted.note_date?._errors?.[0] || "",
      });
      return;
    }

    try {
      const updatedNote = await updateNote(noteToEdit, {
        note_date: editedNoteDate,
        note_title: editedNoteTitle,
        note_description: editedNoteText,
      });

      if (!updatedNote) {
        setToast({
          message: copy.card_note.errors.update_failed,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      const noteId = newUserData.notes.findIndex((n) => n.id === noteToEdit);
      newUserData.notes[noteId] = {
        ...newUserData.notes[noteId],
        note_title: editedNoteTitle,
        note_date: editedNoteDate,
        note_description: editedNoteText,
      };

      setUserData(newUserData);
      setToast({
        message: copy.card_note.errors.update_success,
        type: "success",
      });
      closePopup();
    } catch (err) {
      console.error("Error updating note:", err.message);
      setToast({ message: copy.card_note.errors.update_failed, type: "error" });
    }
  };

  const handleReadClickNote = (noteItem) => {
    setSelectedNote(noteItem);
    openPopup("written-note");
  };

  const addNoteDropdown = [
    {
      text: copy.dropdown.add,
      onClick: () => openPopup("note"),
      icon: <TbCirclePlus />,
    },
  ];

  const noteDropdown = (noteItem) => [
    {
      text: copy.dropdown.view,
      onClick: () => handleReadClickNote(noteItem),
      icon: <TbEye />,
    },
    {
      text: copy.dropdown.edit,
      onClick: () => handleEditClickNote(noteItem),
      icon: <TbEdit />,
    },
    {
      text: copy.dropdown.delete,
      onClick: () => handleDeleteClick(noteItem.id),
      icon: <TbTrash />,
    },
  ];

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}

      {notes.length > 0 ? (
        notes.map((noteItem) => (
          <Card
            key={noteItem.id}
            title={copy.card_note.headline}
            description={copy.card_note.description}
            options={noteDropdown(noteItem)}
            cardClassName={cardClassName || "h-full"}
            useMaxHeight={useMaxHeight}
          >
            <NoteItem
              noteItem={noteItem}
              openPopup={() => handleReadClickNote(noteItem)}
              buttonCTA={copy.dropdown.read_more}
              descriptionClassName={` ${descriptionClassName}`}
              truncateTitle={true}
              truncateDescription={true}
            />
          </Card>
        ))
      ) : (
        <div className="w-full h-full" ref={noteButtonRef}>
          <Card
            title={copy.card_note.headline}
            description={copy.card_note.description}
            startButton={copy.card_note.popup.add_title}
            lottieAnimation={animation}
            onClick={() => openPopup("note")}
            options={addNoteDropdown}
            cardClassName={cardClassName || "h-full"}
            useMaxHeight={useMaxHeight}
            className="justify-center relative p-4"
          />
        </div>
      )}

      {/* Create Note */}
      {popupType === "note" && (
        <Popup
          isOpen={true}
          onClose={closePopup}
          title={copy.card_note.popup.add_title}
          buttonSecondaryCTA={copy.dropdown.cancel}
          onClickSecondaryLink={closePopup}
          buttonPrimaryCTA={copy.dropdown.save}
          onClickPrimaryLink={handleSubmit}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <InputDate
              label={copy.card_note.form.label_date}
              value={note_date}
              onChange={(e) => setNoteDate(e.target.value)}
              error={noteErrors.note_date}
            />
            <div className="flex flex-col gap-1">
              <InputText
                label={copy.card_note.form.label_title}
                placeholder={copy.card_note.form.placeholder_title}
                value={note_title}
                onChange={(e) => setNoteTitle(e.target.value)}
                error={noteErrors.note_title}
                details={`${note_title.length}/200 characters`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <InputTextarea
                label={copy.card_note.form.label_text}
                placeholder={copy.card_note.form.placeholder_text}
                value={note_description}
                onChange={(e) => setNoteDescription(e.target.value)}
                error={noteErrors.note_description}
                details={`${note_description.length}/3000 characters`}
              />
            </div>
          </form>
        </Popup>
      )}

      {/* Delete Note */}
      {popupType === "note-delete" && (
        <Popup
          isOpen={true}
          onClose={closePopup}
          title={copy.card_note.popup.delete_title}
          description={copy.card_note.popup.delete_description}
          buttonSecondaryCTA={copy.dropdown.cancel}
          onClickSecondaryLink={closePopup}
          buttonPrimaryCTA={copy.dropdown.delete}
          onClickPrimaryLink={handleDelete}
        />
      )}

      {/* View Note */}
      {popupType === "written-note" && (
        <Popup
          isOpen={true}
          onClose={closePopup}
          title={copy.card_note.popup.view_title}
          buttonSecondaryCTA={copy.dropdown.edit}
          buttonPrimaryCTA={copy.dropdown.close}
          onClickPrimaryLink={closePopup}
          onClickSecondaryLink={() => handleEditClickNote(selectedNote)}
        >
          {selectedNote && (
            <NoteItem
              noteItem={selectedNote}
              truncateDescription={false}
              truncateTitle={false}
            />
          )}
        </Popup>
      )}

      {/* Edit Note */}
      <Popup
        isOpen={popupType === "note-edit"}
        onClose={closePopup}
        title={copy.card_note.popup.edit_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={handleEditNote}
      >
        <div className="flex flex-col gap-4">
          <InputDate
            label={copy.card_note.form.label_date}
            value={editedNoteDate}
            onChange={(e) => setEditedNoteDate(e.target.value)}
            error={editNoteErrors.note_date}
          />
          <div className="flex flex-col gap-1">
            <InputText
              label={copy.card_note.form.label_title}
              placeholder={copy.card_note.form.placeholder_title}
              value={editedNoteTitle}
              onChange={(e) => setEditedNoteTitle(e.target.value)}
              error={editNoteErrors.note_title}
              details={`${editedNoteTitle.length}/200 characters`}
            />
          </div>
          <div className="flex flex-col gap-1">
            <InputTextarea
              label={copy.card_note.form.label_text}
              placeholder={copy.card_note.form.placeholder_text}
              value={editedNoteText}
              onChange={(e) => setEditedNoteText(e.target.value)}
              error={editNoteErrors.note_description}
              details={`${editedNoteText.length}/3000 characters`}
            />
          </div>
        </div>
      </Popup>
    </>
  );
};

CardAddNote.propTypes = {
  notes: PropTypes.array.isRequired,
  activeDay: PropTypes.instanceOf(DateTime).isRequired,
  descriptionClassName: PropTypes.string,
  onExternalTrigger: PropTypes.bool,
  onExternalViewTrigger: PropTypes.bool,
};

export default CardAddNote;
